/**
 * Doubloon Gift Guide Tab Navigation
 *
 * Shows/hides featured-collection sections on the gift guide page
 * based on tab selection. Supports URL hash deep linking for ads
 * and homepage buttons (e.g., /pages/gift-guide#occasion-fathers-day).
 */

if (!customElements.get('doubloon-gift-tabs')) {
  class DoubloonGiftTabs extends HTMLElement {
    constructor() {
      super();

      // Section ID groups — keys match data-section attributes on pills,
      // values are Shopify section wrapper IDs in the page template.
      this.groups = {
        occasion: ['occasion_fathers_day', 'occasion_retirement', 'occasion_hole_in_one'],
        budget: ['budget_under_100', 'budget_100_250', 'budget_250_plus'],
      };

      this.allSectionKeys = [...this.groups.occasion, ...this.groups.budget];
    }

    connectedCallback() {
      this.tabs = this.querySelectorAll('.doubloon-gift-tabs__tab');
      this.subBars = this.querySelectorAll('.doubloon-gift-tabs__sub-bar');
      this.pills = this.querySelectorAll('.doubloon-gift-tabs__pill');

      this._onTabClick = (e) => this.handleTabClick(e);
      this._onPillClick = (e) => this.handlePillClick(e);

      this.tabs.forEach((tab) => tab.addEventListener('click', this._onTabClick));
      this.pills.forEach((pill) => pill.addEventListener('click', this._onPillClick));

      this.handleHash();
    }

    disconnectedCallback() {
      this.tabs.forEach((tab) => tab.removeEventListener('click', this._onTabClick));
      this.pills.forEach((pill) => pill.removeEventListener('click', this._onPillClick));
    }

    handleTabClick(e) {
      const group = e.currentTarget.dataset.show;

      // Update active tab
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.show === group;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive);
      });

      // Show/hide sub-bars
      this.subBars.forEach((bar) => {
        bar.hidden = bar.dataset.group !== group;
      });

      if (group === 'all') {
        this.showAllSections();
        this.updateHash('');
      } else {
        // Show only sections in this group, hide others
        this.showGroup(group);

        // Activate the first pill's section by default
        const firstPill = this.querySelector(
          `.doubloon-gift-tabs__sub-bar[data-group="${group}"] .doubloon-gift-tabs__pill`
        );
        if (firstPill) {
          this.activatePill(firstPill);
        }
      }
    }

    handlePillClick(e) {
      this.activatePill(e.currentTarget);
    }

    activatePill(pill) {
      const sectionKey = pill.dataset.section;
      const group = pill.closest('.doubloon-gift-tabs__sub-bar')?.dataset.group;

      // Update active pill within the same group
      const siblings = pill.parentElement.querySelectorAll('.doubloon-gift-tabs__pill');
      siblings.forEach((p) => p.classList.toggle('is-active', p === pill));

      // Show only the selected section, hide others in this group
      if (group && this.groups[group]) {
        this.groups[group].forEach((key) => {
          this.toggleSection(key, key === sectionKey);
        });
      }

      // Hide sections from other groups
      const otherGroup = group === 'occasion' ? 'budget' : 'occasion';
      if (this.groups[otherGroup]) {
        this.groups[otherGroup].forEach((key) => {
          this.toggleSection(key, false);
        });
      }

      this.updateHash(sectionKey.replace(/_/g, '-'));
    }

    showAllSections() {
      this.allSectionKeys.forEach((key) => this.toggleSection(key, true));
    }

    showGroup(group) {
      // Show all sections in the group
      if (this.groups[group]) {
        this.groups[group].forEach((key) => this.toggleSection(key, true));
      }

      // Hide sections NOT in this group
      Object.keys(this.groups).forEach((g) => {
        if (g !== group) {
          this.groups[g].forEach((key) => this.toggleSection(key, false));
        }
      });
    }

    toggleSection(sectionKey, visible) {
      // Shopify wraps template sections with IDs like
      // "shopify-section-template--{id}__{key}", so match by suffix.
      const el = document.querySelector(`[id$="__${sectionKey}"]`);
      if (el) {
        el.classList.toggle('doubloon-gift-section--hidden', !visible);
      }
    }

    updateHash(hash) {
      if (hash) {
        history.replaceState(null, '', `#${hash}`);
      } else {
        history.replaceState(null, '', window.location.pathname);
      }
    }

    handleHash() {
      const hash = window.location.hash.slice(1);
      if (!hash) return;

      // Convert hash to section key (e.g., "occasion-fathers-day" → "occasion_fathers_day")
      const sectionKey = hash.replace(/-/g, '_');

      // Find which group this belongs to
      for (const [group, keys] of Object.entries(this.groups)) {
        if (keys.includes(sectionKey)) {
          // Activate the group tab
          const groupTab = this.querySelector(`.doubloon-gift-tabs__tab[data-show="${group}"]`);
          if (groupTab) {
            groupTab.click();
          }

          // Activate the specific pill
          const pill = this.querySelector(`.doubloon-gift-tabs__pill[data-section="${sectionKey}"]`);
          if (pill) {
            // Small delay to let the group tab click complete
            requestAnimationFrame(() => this.activatePill(pill));
          }
          return;
        }
      }
    }
  }

  customElements.define('doubloon-gift-tabs', DoubloonGiftTabs);
}
